import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import headerCloseIcon from '../../assets/header-close-icon.svg';
import headerCloseRing from '../../assets/header-close-ring.svg';
import iconAboutSrc from '../../assets/profile-drawer/icon-about.png';
import iconLogoutSrc from '../../assets/profile-drawer/icon-logout.png';
import iconReferralsSrc from '../../assets/profile-drawer/icon-referrals.png';
import iconTermsSrc from '../../assets/profile-drawer/icon-terms.png';
import iconTransactionsSrc from '../../assets/profile-drawer/icon-transactions.png';
import iconWalletSrc from '../../assets/profile-drawer/icon-wallet.png';
import { iconPrivacySrc } from '../../assets/profile';
import {
  MY_PROFILE_PATH,
  MY_REWARD_PATH,
  MY_WALLET_PATH,
  PRIVACY_PATH,
  REFERRALS_PATH,
  TERMS_PATH,
  TRANSACTIONS_PATH,
} from '../../routes/paths';
import { ABOUT_US_URL } from '../../config/profileMenuConfig';
import ProfileMenuPill from './ProfileMenuPill';
import ProfileRapCard from './ProfileRapCard';

const DRAWER_WIDTH = 456;

const drawerPaperSx = {
  width: { xs: '100%', sm: DRAWER_WIDTH },
  maxWidth: DRAWER_WIDTH,
  background: 'linear-gradient(201.36deg, #1a0834 0.45%, #330030 51.3%, #1a0834 100.23%)',
  borderRadius: { xs: 0, sm: '36px 0 0 36px' },
  boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.45)',
  overflow: 'hidden',
};

const menuItems = [
  { id: 'wallet', label: 'My Wallet', icon: iconWalletSrc, iconSize: 24, path: MY_WALLET_PATH },
  { id: 'rewards', label: 'My Rewards', iconVariant: 'my-rewards', path: MY_REWARD_PATH, iconSize: 24 },
  { id: 'transactions', label: 'Transactions', icon: iconTransactionsSrc, iconSize: 24, path: TRANSACTIONS_PATH },
  { id: 'referrals', label: 'Referrals', icon: iconReferralsSrc, iconSize: 24, path: REFERRALS_PATH },
  { id: 'about', label: 'About Us', icon: iconAboutSrc, iconSize: 24, externalUrl: ABOUT_US_URL },
  { id: 'terms', label: 'Terms & Conditions', icon: iconTermsSrc, iconSize: 24, path: TERMS_PATH },
  { id: 'privacy', label: 'Privacy Policy', icon: iconPrivacySrc, iconSize: 24, path: PRIVACY_PATH },
  { id: 'logout', label: 'Logout', icon: iconLogoutSrc, action: 'logout', iconSize: 24 },
];

function ProfileDrawerCloseButton({ onClick }) {
  return (
    <IconButton
      onClick={onClick}
      aria-label="Close profile menu"
      sx={{
        width: 36,
        height: 36,
        p: 0,
        borderRadius: '100px',
        background: 'linear-gradient(0deg, #5A04D7 0%, #FF00F2 100%)',
        flexShrink: 0,
        '&:hover': {
          background: 'linear-gradient(0deg, #5A04D7 0%, #FF00F2 100%)',
          opacity: 0.92,
        },
      }}
    >
      <Box sx={{ position: 'relative', width: 24, height: 24 }}>
        <Box
          component="img"
          src={headerCloseRing}
          alt=""
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        />
        <Box
          component="img"
          src={headerCloseIcon}
          alt=""
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 12,
            height: 12,
          }}
        />
      </Box>
    </IconButton>
  );
}

export default function ProfileDrawer({ open, onClose, onLogoutClick }) {
  const navigate = useNavigate();

  const handleMenuClick = (item) => {
    if (item.action === 'logout') {
      onClose();
      onLogoutClick?.();
      return;
    }

    if (item.externalUrl) {
      onClose();
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (item.path) {
      onClose();
      navigate(item.path);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: drawerPaperSx }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: 'rgba(8, 2, 18, 0.72)' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          px: { xs: 2, sm: '28px' },
          py: { xs: 2.5, sm: 3 },
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            position: 'relative',
            minHeight: 36,
          }}
        >
          <Box sx={{ width: 36, flexShrink: 0 }} />
          <Typography
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: 24,
              lineHeight: '32px',
              letterSpacing: '0.8px',
              color: '#fff',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            MY PROFILE
          </Typography>
          <ProfileDrawerCloseButton onClick={onClose} />
        </Box>

        <ProfileRapCard
          onClick={() => {
            onClose();
            navigate(MY_PROFILE_PATH);
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '14px',
            width: '100%',
            pb: 2,
          }}
        >
          {menuItems.map((item) => (
            <ProfileMenuPill
              key={item.id}
              icon={item.icon}
              iconVariant={item.iconVariant}
              iconSize={item.iconSize}
              label={item.label}
              onClick={() => handleMenuClick(item)}
            />
          ))}
        </Box>
      </Box>
    </Drawer>
  );
}
